/** Batch "Assign error series ID" dialog for the delivery-logs grid (a custom
 *  `dialog(ctx)` batch action).
 *
 *  Stamps an arbitrary short string onto each selected log row's
 *  `error_series_id` — the console's way of grouping related problem
 *  deliveries under one label so they can be found again (`series=<id>` in
 *  the search). Plain string, not tenant-scoped — no cross-tenant layer.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

/** Event_Delivery_Log.error_series_id is a CharField(32). */
const MAX_SERIES_LENGTH = 32;

export function AssignSeriesDialog({ ctx }: { ctx: BatchActionContext }) {
  const [seriesId, setSeriesId] = useState('');

  const trimmed = seriesId.trim();
  const tooLong = trimmed.length > MAX_SERIES_LENGTH;
  const canSubmit = trimmed.length > 0 && !tooLong;

  const submit = () => {
    if (!canSubmit) return;
    void ctx.runStandard({ series_id: trimmed });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Assign error series ID</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Tag {ctx.count.toLocaleString()} delivery log(s) with a series ID so they can be
            found together later (<code>series=…</code> in the search). Overwrites any existing
            series ID on the selected rows. Runs in the background.
          </Typography>

          <TextField
            autoFocus
            required
            size="small"
            label="Series ID"
            value={seriesId}
            onChange={(e) => setSeriesId(e.target.value)}
            error={tooLong}
            helperText={
              tooLong
                ? `Max ${MAX_SERIES_LENGTH} characters.`
                : 'Any short label, e.g. an incident id (INC-42).'
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} delivery logs.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
